import { useCallback, useEffect, useRef, useState } from "react";

import { fetchLensContent } from "../../../data/queries/lens-tester";
import { ViewerArtifacts } from "../../../data/queries/viewer";
import { Artifact } from "../../../types/artifact";
import { useAccount } from "../../account/hooks/useAccount";
import { useArtifactDetails } from "../../viewer/hooks/useArtifactDetails";
import { ADJACENT_OFFSET, EMPTY_LENGTH } from "../constants/numeric";
import { LENS_NAVIGATION_DIRECTION, LensNavigationDirection } from "../utils/lensNavigation";
import { useArtifactDataTransform } from "./useArtifactDataTransform";
import { useHistoryModal } from "./useHistoryModal";
import { useLensData } from "./useLensData";
import { useLensForm } from "./useLensForm";
import { useLensHistoryForInteraction } from "./useLensHistoryForInteraction";
import { useLensTestWithHistory } from "./useLensTestWithHistory";

interface UseLensTesterParams {
  open: boolean;
  selectedArtifact: Artifact | null;
}

export interface UseLensTesterResult {
  currentLensExpanded: boolean;
  setCurrentLensExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  testLensExpanded: boolean;
  setTestLensExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  selectedLensPath: string;
  isLoadingLens: boolean;
  historyModal: ReturnType<typeof useHistoryModal>;
  interactionId: string | null;
  artifactData: ReturnType<typeof useArtifactDetails>["data"];
  currentLensName: string | undefined;
  lensData: ReturnType<typeof useLensData>["data"];
  isLoadingLensData: boolean;
  lensTestWithHistory: ReturnType<typeof useLensTestWithHistory>;
  interactionHistory: ReturnType<typeof useLensHistoryForInteraction>;
  form: ReturnType<typeof useLensForm>;
  testResultsData: ViewerArtifacts;
  handleNavigate: (direction: LensNavigationDirection) => void;
  handleLensSelection: (fullPath: string) => Promise<void>;
  handleTest: () => Promise<void>;
  allHistoryLength: number;
  isHistoryEmpty: boolean;
}

interface LocalState {
  currentLensExpanded: boolean;
  setCurrentLensExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  testLensExpanded: boolean;
  setTestLensExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  selectedLensPath: string;
  setSelectedLensPath: React.Dispatch<React.SetStateAction<string>>;
  isLoadingLens: boolean;
  setIsLoadingLens: React.Dispatch<React.SetStateAction<boolean>>;
  hasInitialized: React.MutableRefObject<boolean>;
  shouldNavigateToLatest: boolean;
  setShouldNavigateToLatest: React.Dispatch<React.SetStateAction<boolean>>;
}

const useLocalState = (): LocalState => {
  const [currentLensExpanded, setCurrentLensExpanded] = useState<boolean>(true);
  const [testLensExpanded, setTestLensExpanded] = useState<boolean>(true);
  const [selectedLensPath, setSelectedLensPath] = useState<string>("");
  const [isLoadingLens, setIsLoadingLens] = useState<boolean>(false);
  const hasInitialized = useRef(false);
  const [shouldNavigateToLatest, setShouldNavigateToLatest] = useState<boolean>(false);
  return {
    currentLensExpanded, setCurrentLensExpanded,
    testLensExpanded, setTestLensExpanded,
    selectedLensPath, setSelectedLensPath,
    isLoadingLens, setIsLoadingLens,
    hasInitialized,
    shouldNavigateToLatest, setShouldNavigateToLatest
  };
};

interface InitEffectParams {
  open: boolean;
  hasInitialized: React.MutableRefObject<boolean>;
  clearResultsRef: React.MutableRefObject<() => void>;
  resetFormRef: React.MutableRefObject<() => void>;
  setSelectedLensPath: (path: string) => void;
}

const useInitEffect = ({ open, hasInitialized, clearResultsRef, resetFormRef, setSelectedLensPath }: InitEffectParams): void => {
  useEffect(() => {
    if (open && !hasInitialized.current) {
      clearResultsRef.current();
      resetFormRef.current();
      setSelectedLensPath("");
      hasInitialized.current = true;
      return;
    }
    if (!open) {
      hasInitialized.current = false;
    }
  }, [open, hasInitialized, clearResultsRef, resetFormRef, setSelectedLensPath]);
};

interface NavigateToLatestParams {
  historyLength: number;
  shouldNavigateToLatest: boolean;
  setShouldNavigateToLatest: (value: boolean) => void;
  navigateHistoryRef: React.MutableRefObject<(direction: LensNavigationDirection) => void>;
}

const useNavigateToLatest = (params: NavigateToLatestParams): void => {
  const { historyLength, shouldNavigateToLatest, setShouldNavigateToLatest, navigateHistoryRef } = params;
  useEffect(() => {
    if (shouldNavigateToLatest) {
      navigateHistoryRef.current(LENS_NAVIGATION_DIRECTION.Previous);
      setShouldNavigateToLatest(false);
    }
  }, [historyLength, shouldNavigateToLatest, setShouldNavigateToLatest, navigateHistoryRef]);
};

interface LensSelectionParams {
  account: ReturnType<typeof useAccount>["account"];
  setSelectedLensPath: (path: string) => void;
  setIsLoadingLens: (loading: boolean) => void;
  form: ReturnType<typeof useLensForm>;
}

const useHandleLensSelection = (params: LensSelectionParams): ((fullPath: string) => Promise<void>) => {
  const { account, setSelectedLensPath, setIsLoadingLens, form } = params;
  return useCallback(async (fullPath: string): Promise<void> => {
    if (!fullPath || !account?.spoke_tenant_id) {
      return;
    }
    setSelectedLensPath(fullPath);
    setIsLoadingLens(true);
    const { success, data } = await fetchLensContent(account.spoke_tenant_id, fullPath);
    if (!success) {
      setIsLoadingLens(false);
      return;
    }
    form.setTestLens(data);
    setIsLoadingLens(false);
  }, [account, form, setSelectedLensPath, setIsLoadingLens]);
};

interface HandleTestParams {
  account: ReturnType<typeof useAccount>["account"];
  form: ReturnType<typeof useLensForm>;
  transformedArtifactData: ReturnType<typeof useArtifactDataTransform>;
  lensTestWithHistory: ReturnType<typeof useLensTestWithHistory>;
  interactionId: string | null;
  interactionHistory: ReturnType<typeof useLensHistoryForInteraction>;
  setShouldNavigateToLatest: (value: boolean) => void;
}

const useHandleTest = (params: HandleTestParams): (() => Promise<void>) => {
  const { account, form, transformedArtifactData, lensTestWithHistory,
    interactionId, interactionHistory, setShouldNavigateToLatest } = params;
  return useCallback(async (): Promise<void> => {
    if (!account || !form.testLens.trim() || !transformedArtifactData || form.lensName.trim() === "") {
      return;
    }
    lensTestWithHistory.clearResults();
    const config = form.getFormConfig(account.name, account.description);
    await lensTestWithHistory.testLens({
      config,
      artifactData: transformedArtifactData,
      interactionId: interactionId ? interactionId : undefined,
      tenantId: account.spoke_tenant_id ? account.spoke_tenant_id : undefined,
      tenantName: account.name
    });
    setShouldNavigateToLatest(true);
    interactionHistory.refreshHistory();
  }, [account, form, transformedArtifactData, lensTestWithHistory, interactionId, interactionHistory, setShouldNavigateToLatest]);
};

interface NavigateParams {
  interactionHistory: ReturnType<typeof useLensHistoryForInteraction>;
  lensTestWithHistory: ReturnType<typeof useLensTestWithHistory>;
  form: ReturnType<typeof useLensForm>;
}

const useHandleNavigate = ({ interactionHistory, lensTestWithHistory, form }: NavigateParams): ((direction: LensNavigationDirection) => void) =>
  useCallback((direction: LensNavigationDirection): void => {
    if (direction === LENS_NAVIGATION_DIRECTION.Next
        && interactionHistory.currentIndex === interactionHistory.interactionHistory.length - ADJACENT_OFFSET) {
      lensTestWithHistory.clearResults();
      form.resetForm();
    }
    interactionHistory.navigateHistory(direction);
  }, [interactionHistory, lensTestWithHistory, form]);

interface BuildTestResultsParams {
  interactionHistory: ReturnType<typeof useLensHistoryForInteraction>;
}

const buildTestResultsData = ({ interactionHistory }: BuildTestResultsParams): ViewerArtifacts => {
  const displayTestResults = interactionHistory.isInNewVersionState
    ? null
    : interactionHistory.currentHistoryItem?.lastResult || null;
  return {
    scorecard: {
      content: { scorecard: displayTestResults },
      type: "scorecard",
      modified: interactionHistory.currentHistoryItem?.createdAt || new Date().toISOString()
    },
    all: []
  };
};

interface CoreData {
  interactionId: string | null;
  account: ReturnType<typeof useAccount>["account"];
  artifactData: ReturnType<typeof useArtifactDetails>["data"];
  currentLensName: string | undefined;
  lensData: ReturnType<typeof useLensData>["data"];
  isLoadingLensData: boolean;
  lensTestWithHistory: ReturnType<typeof useLensTestWithHistory>;
  transformedArtifactData: ReturnType<typeof useArtifactDataTransform>;
  interactionHistory: ReturnType<typeof useLensHistoryForInteraction>;
  historyModal: ReturnType<typeof useHistoryModal>;
  form: ReturnType<typeof useLensForm>;
}

const useCoreData = (selectedArtifact: Artifact | null): CoreData => {
  const interactionId = selectedArtifact?.interaction_id || null;
  const { account } = useAccount();
  const { data: artifactData } = useArtifactDetails(interactionId);
  const { protocol_file: currentLensName } = artifactData?.protocolRouter?.content || {};

  const { data: lensData, isLoading: isLoadingLensData } = useLensData(currentLensName);
  const lensTestWithHistory = useLensTestWithHistory();
  const transformedArtifactData = useArtifactDataTransform(artifactData);
  const interactionHistory = useLensHistoryForInteraction(interactionId);
  const historyModal = useHistoryModal(interactionHistory.allHistory);

  const form = useLensForm(
    interactionHistory.isInNewVersionState ? null : interactionHistory.currentHistoryItem,
    interactionHistory.isInNewVersionState
  );

  return {
    interactionId, account, artifactData, currentLensName, lensData, isLoadingLensData,
    lensTestWithHistory, transformedArtifactData, interactionHistory, historyModal, form
  };
};

interface StableRefs {
  clearResultsRef: React.MutableRefObject<() => void>;
  resetFormRef: React.MutableRefObject<() => void>;
  navigateHistoryRef: React.MutableRefObject<(direction: LensNavigationDirection) => void>;
}

const useStableRefs = (core: CoreData): StableRefs => {
  const clearResultsRef = useRef(core.lensTestWithHistory.clearResults);
  clearResultsRef.current = core.lensTestWithHistory.clearResults;
  const resetFormRef = useRef(core.form.resetForm);
  resetFormRef.current = core.form.resetForm;
  const navigateHistoryRef = useRef(core.interactionHistory.navigateHistory);
  navigateHistoryRef.current = core.interactionHistory.navigateHistory;
  return { clearResultsRef, resetFormRef, navigateHistoryRef };
};

export const useLensTester = ({ open, selectedArtifact }: UseLensTesterParams): UseLensTesterResult => {
  const local = useLocalState();
  const core = useCoreData(selectedArtifact);
  const refs = useStableRefs(core);

  useInitEffect({
    open,
    hasInitialized: local.hasInitialized,
    clearResultsRef: refs.clearResultsRef,
    resetFormRef: refs.resetFormRef,
    setSelectedLensPath: local.setSelectedLensPath
  });

  useNavigateToLatest({
    historyLength: core.interactionHistory.interactionHistory.length,
    shouldNavigateToLatest: local.shouldNavigateToLatest,
    setShouldNavigateToLatest: local.setShouldNavigateToLatest,
    navigateHistoryRef: refs.navigateHistoryRef
  });

  const handleNavigate = useHandleNavigate({
    interactionHistory: core.interactionHistory, lensTestWithHistory: core.lensTestWithHistory, form: core.form
  });
  const handleLensSelection = useHandleLensSelection({
    account: core.account, setSelectedLensPath: local.setSelectedLensPath, setIsLoadingLens: local.setIsLoadingLens, form: core.form
  });
  const handleTest = useHandleTest({
    account: core.account, form: core.form, transformedArtifactData: core.transformedArtifactData,
    lensTestWithHistory: core.lensTestWithHistory, interactionId: core.interactionId,
    interactionHistory: core.interactionHistory, setShouldNavigateToLatest: local.setShouldNavigateToLatest
  });

  return {
    currentLensExpanded: local.currentLensExpanded, setCurrentLensExpanded: local.setCurrentLensExpanded,
    testLensExpanded: local.testLensExpanded, setTestLensExpanded: local.setTestLensExpanded,
    selectedLensPath: local.selectedLensPath, isLoadingLens: local.isLoadingLens,
    historyModal: core.historyModal,
    interactionId: core.interactionId, artifactData: core.artifactData, currentLensName: core.currentLensName,
    lensData: core.lensData, isLoadingLensData: core.isLoadingLensData,
    lensTestWithHistory: core.lensTestWithHistory, interactionHistory: core.interactionHistory,
    form: core.form, testResultsData: buildTestResultsData({ interactionHistory: core.interactionHistory }),
    handleNavigate, handleLensSelection, handleTest,
    allHistoryLength: core.interactionHistory.allHistory.length,
    isHistoryEmpty: core.interactionHistory.allHistory.length === EMPTY_LENGTH
  };
};
